// ----------------------------------------------------------------------
// <copyright file="Izenda.TenantGroupingList.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { observable } from 'mobx';
import { map, find, isNil, head } from 'lodash/fp';
import TenantGroupingDetails from "./Izenda.TenantGroupingDetails";
import { EMPTY_GUID } from 'logic/data/Izenda.ReportsConstants';

/**
 * List of tenant groups
 */
export default class TenantGroupingList {

  /**
   * @type {TenantGroupingDetails[]}
   */
  @observable groupings = [];

  /**
   * Read raw json data.
   * @param {Array} data Raw list of tenant groups
   */
  populateData(data) {
    this.groupings = map(x => {
      const grouping = new TenantGroupingDetails();
      grouping.populate(x);
      return grouping;
    }, data || []);
  }

  /**
   * Prepare data to send to the backend
   * @returns {Array} Serialized groupings
   */
  toJson() {
    return map(x => x.toJson(), this.groupings || []);
  }

  /**
   * Add grouping to list.
   * @param {TenantGroupingDetails} tenantGroupingDetails New tenant group.
   */
  addItem(tenantGroupingDetails) {
    this.groupings.push(tenantGroupingDetails);
  }

  /**
   * Find grouping with given name
   * @param {string} name
   * @returns {TenantGroupingDetails} found grouping or null
   */
  findGrouping(name) {
    return find({name: name}, this.groupings) || null;
  }

  /**
   * Find grouping with given id
   * @param {string} id
   * @returns {TenantGroupingDetails} found grouping or null
   */
  findGroupingById(id) {
    return find({id: id}, this.groupings) || null;
  }

  /**
   * Check whether grouping with this name exist or not
   * @param {string} name Grouping name.
   */
  isGroupingExist(name) {
    return !isNil(this.findGrouping(name));
  }

  /**
   * Add new grouping
   * @param {string} groupingName New grouping name
   * @returns {TenantGroupingDetails} new grouping object or null if wasn't able to add.
   */
  addNewItem(groupingName) {
    if (this.isGroupingExist(groupingName)) {
      return null;
    }
    const newGrouping = new TenantGroupingDetails();
    newGrouping.id = EMPTY_GUID;
    newGrouping.name = groupingName;
    this.addItem(newGrouping);
    return newGrouping;
  }

  clear() {
    this.groupings = [];
  }
}



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.TenantGroupingList.js